import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import MainCard from 'components/MainCard';
import { useSubStores } from 'contexts/SubStoreContext';
// Removed unnecessary import since we're using cached data
// import { fetchSubstoreDetails } from 'api/substoreDetails';
// import { useEmployees } from 'contexts/EmployeeContext';
import * as XLSX from 'xlsx';
import api from 'api/axios';
import { fetchEmployees, fetchPaginatedEmployees, deleteEmployee } from 'api/employees';
import { createEmployee } from 'api/employeeCreate';
import UploadExcelPopup from './BulkUpload';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { FilterAlt as FilterIcon } from '@mui/icons-material';

// ==============================|| EMPLOYEES PAGE ||============================== //

const defaultEmployee = {
  username: '',
  password: 'Password',
  email: '',
  phone: '',
  substore_id: '',
  employment_type: '',
  work_hours: 0,
  address: ''
};

export default function Employees() {
  const location = useLocation();
  const navigate = useNavigate();
  const { substores } = useSubStores();
  const [backendSubstores, setBackendSubstores] = useState([]);

  // State for regular employee list
  const [employees, setEmployees] = useState([]);
  const [selectedSubstore, setSelectedSubstore] = useState(0);
  const [search, setSearch] = useState('');
  const [searchResult, setSearchResult] = useState([]);

  // State for paginated employee list
  const [paginatedEmployees, setPaginatedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [ordering, setOrdering] = useState('username');
  const [substoreFilter, setSubstoreFilter] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [removeIdx, setRemoveIdx] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState(defaultEmployee);
  
  const fileInputRef = useRef();

  // Fetch employees from cache or backend on mount
  useEffect(() => {
    const cached = localStorage.getItem('employees');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setEmployees(parsed);
        } else {
          setEmployees([]);
        }
      } catch (e) {
        setEmployees([]);
      }
    } else {
      const loadEmployees = async () => {
        try {
          const data = await fetchEmployees();
          setEmployees(data);
          localStorage.setItem('employees', JSON.stringify(data));
        } catch (err) {
          setEmployees([]);
        }
      };
      loadEmployees();
    }

    // Load paginated employees
    loadPaginatedEmployees();

    // Use substores from context instead of fetching them again
    if (substores && substores.length > 0) {
      setBackendSubstores(
        substores.map((store) => ({
          id: store.id,
          name: store.storeName || store.name
        }))
      );
    }
  }, [substores]);

  // Load paginated employees from the backend
  const loadPaginatedEmployees = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1, // API uses 1-based pagination
        page_size: rowsPerPage
      };

      // Add optional filters if they exist
      if (searchTerm) params.search = searchTerm;
      if (ordering) params.ordering = ordering;
      if (substoreFilter) params.substore = substoreFilter;

      const data = await fetchPaginatedEmployees(params);
      setPaginatedEmployees(data.results);
      setTotalCount(data.count);
    } catch (err) {
      console.error('Failed to fetch paginated employees:', err);
      setPaginatedEmployees([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Reload data when pagination, search, or filters change
  useEffect(() => {
    loadPaginatedEmployees();
  }, [page, rowsPerPage, searchTerm, ordering, substoreFilter]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search handler for paginated view
  const handlePaginatedSearch = () => {
    setPage(0); // Reset to first page when searching
    setSearchTerm(search);
  };

  // Sorting handler
  const handleSort = (column) => {
    // Toggle between ascending and descending
    if (ordering === column) {
      setOrdering(`-${column}`); // Descending order
    } else if (ordering === `-${column}`) {
      setOrdering(''); // Remove sorting
    } else {
      setOrdering(column); // Ascending order
    }
    setPage(0); // Reset to first page when sorting changes
  };

  // Show all employees (no filtering)
  const currentEmployees = employees;

  // Debug logging
  console.log('Employees Page - All employees:', employees?.length || 0);
  console.log('Employees Page - Current substore:', selectedSubstore);
  console.log('Employees Page - Current employees:', currentEmployees?.length || 0);

  // Check for navigation state to pre-select substore
  useEffect(() => {
    if (location.state?.selectedSubstore !== undefined) {
      setSubstoreFilter(location.state.selectedSubstore);
      // Clear the state to prevent re-selection on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Search handler
  const handleSearch = () => {
    setSearchResult(currentEmployees.filter((e) => e.fullName.toLowerCase().includes(search.trim().toLowerCase())));
  };

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);

const handleExcelUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  
  // Reset status and set uploading
  setUploadStatus({ status: 'uploading' });
  
  const formData = new FormData();
  formData.append('file', file);
  try {
    const res = await api.post('/core/owner/employees/bulk_upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const data = res.data;
    
    // Set completed status with data
    setUploadStatus({ 
      status: 'completed', 
      data: {
        created: data.created || [],
        errors: data.errors || []
      }
    });
    
    console.log(data.errors); // <-- Add this to see error details
    
    // Refresh employee list after successful upload
    loadPaginatedEmployees();
  } catch (err) {
    const resp = err.response?.data;
    const detail = resp?.detail || 'Upload failed';
    
    // Set error status
    setUploadStatus({ 
      status: 'error', 
      message: detail 
    });
    
    const errors = Array.isArray(resp?.errors) ? `\nErrors: ${JSON.stringify(resp.errors).slice(0, 1000)}` : '';
    const trace = resp?.trace ? `\nTrace: ${resp.trace.substring(0, 500)}` : '';
    console.error('Bulk upload error:', err.response || err);
    alert(detail + errors + trace);
  } finally {
    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  }
};

// Reset upload status when dialog closes
const handleCloseUpload = () => {
  setUploadOpen(false);
  // Keep status visible for a moment, then reset after a delay
  setTimeout(() => {
    setUploadStatus(null);
  }, 3000);
};

  // Add employee dialog handlers
  const openAddDialog = () => {
    // Use substoreFilter if available, otherwise use selectedSubstore
    const substoreId = substoreFilter || backendSubstores[0]?.id || 0;
    setNewEmployee({ ...defaultEmployee, substore_id: substoreId });
    setAddOpen(true);
  };

  const closeAddDialog = () => setAddOpen(false);

  const handleAddEmployee = async () => {
    // Validate required fields
    if (!newEmployee.username.trim() || !newEmployee.email.trim() || !newEmployee.phone.trim() || !newEmployee.employment_type.trim() || !newEmployee.work_hours.trim() || !newEmployee.address.trim()) return;

    try {
      // Convert substore_id to number if it's a string
      const employeeData = {
        ...newEmployee,
        substore_id: parseInt(newEmployee.substore_id, 10) || 0
      };

      console.log('Creating employee with data:', employeeData);
      const created = await createEmployee(employeeData);

      // Update both employee lists
      setEmployees((prev) => [...prev, created]);

      // Refresh paginated list
      loadPaginatedEmployees();

      setAddOpen(false);
    } catch (err) {
      console.error('Failed to create employee:', err);
      alert(`Failed to create employee: ${err.response?.data?.error || err.message || 'Unknown error'}`);
    }
  };

  const openRemoveDialog = () => {}; // Empty function to prevent errors
  const closeRemoveDialog = () => setConfirmOpen(false);
  const handleRemoveEmployee = async () => {
    try {
      await deleteEmployee(removeIdx);
      setPaginatedEmployees(prev => prev.filter(emp => emp.id !== removeIdx));
      setEmployees(prev => prev.filter(emp => emp.id !== removeIdx));
      setConfirmOpen(false);
      setRemoveIdx(null);
    } catch (err) {
      alert('Failed to delete employee.');
      setConfirmOpen(false);
      setRemoveIdx(null);
    }
  };

  // Handle employee name click to navigate to details
  const handleEmployeeClick = (employeeId) => {
    navigate(`/employee/${employeeId}`);
  };

  // Which list to show
  const listToShow = search.trim() ? searchResult : currentEmployees;

  return (
    <MainCard title="Employees">
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 150, height:35 }}>
          <InputLabel>Select Sub Store</InputLabel>
          <Select value={substoreFilter} label="Select Sub Store" onChange={(e) => setSubstoreFilter(e.target.value) } sx={{ height: 35, padding: '0 6px' }}>
            <MenuItem value={0}>All Substores</MenuItem>
            {backendSubstores.map((store) => (
              <MenuItem key={store.id} value={store.id}>
                {store.name || store.storeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
          <TextField
            label="Search Employee"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePaginatedSearch();
            }}
            fullWidth
          />
          <Button variant="outlined" startIcon={<SearchIcon />} onClick={handlePaginatedSearch}>
            Search
          </Button>
        </Box>
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setUploadOpen(true)}>
          Upload Excel
        </Button>
        <Button variant="contained" onClick={openAddDialog}>
          Add
        </Button>
      </Box>

      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Employee Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total Employees: {totalCount} | Current Store:{' '}
          {substoreFilter
            ? backendSubstores.find((s) => s.id === parseInt(substoreFilter) || s.id === substoreFilter)?.name ||
              backendSubstores.find((s) => s.id === parseInt(substoreFilter) || s.id === substoreFilter)?.storeName ||
              'Unknown'
            : 'All Stores'}
        </Typography>
        {searchTerm && (
          <Chip
            label={`Search: ${searchTerm}`}
            onDelete={() => {
              setSearch('');
              setSearchTerm('');
            }}
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell onClick={() => handleSort('username')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Name
                  {ordering === 'username' && ' ↑'}
                  {ordering === '-username' && ' ↓'}
                </TableCell>
                <TableCell onClick={() => handleSort('email')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Email
                  {ordering === 'email' && ' ↑'}
                  {ordering === '-email' && ' ↓'}
                </TableCell>
                <TableCell onClick={() => handleSort('phone')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Phone
                  {ordering === 'phone' && ' ↑'}
                  {ordering === '-phone' && ' ↓'}
                </TableCell>
                <TableCell onClick={() => handleSort('substore')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Substore
                  {ordering === 'substore' && ' ↑'}
                  {ordering === '-substore' && ' ↓'}
                </TableCell>
                <TableCell onClick={() => handleSort('employment_type')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  {ordering === 'employment_type' && ' ↑'}
                  {ordering === '-employment_type' && ' ↓'}
                  Employment Type</TableCell>
                <TableCell onClick={() => handleSort('work_hours')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Work Hours
                  {ordering === 'work_hours' && ' ↑'}
                  {ordering === '-work_hours' && ' ↓'}
                </TableCell>
                <TableCell onClick={() => handleSort('address')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Address
                  {ordering === 'address' && ' ↑'}
                  {ordering === '-address' && ' ↓'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={30} /> <Typography sx={{ ml: 2 }}>Loading employees...</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">No employees found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedEmployees.map((emp) => (
                  <TableRow key={emp.id} hover onClick={() => handleEmployeeClick(emp.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography fontWeight={600} color="primary">
                        {emp.fullName || emp.username}
                      </Typography>
                    </TableCell>
                    <TableCell>{emp.email}</TableCell>
                    <TableCell>{emp.phone}</TableCell>
                    <TableCell>{emp.substore?.name || 'Unknown'}</TableCell>
                    <TableCell>{emp.employment_type}</TableCell>
                    <TableCell>{emp.work_hours}</TableCell>
                    <TableCell>{emp.address}</TableCell>  
                    <TableCell>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={e => {
                        e.stopPropagation();
                        setRemoveIdx(emp.id);
                        setConfirmOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Add Employee Dialog */}
      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add Employee</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Username"
              value={newEmployee.username}
              onChange={(e) => setNewEmployee((emp) => ({ ...emp, username: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newEmployee.email}
              onChange={(e) => setNewEmployee((emp) => ({ ...emp, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Phone Number"
              value={newEmployee.phone}
              onChange={(e) => setNewEmployee((emp) => ({ ...emp, phone: e.target.value }))}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Substore</InputLabel>
              <Select
                value={newEmployee.substore_id}
                label="Substore"
                onChange={(e) => setNewEmployee((emp) => ({ ...emp, substore_id: e.target.value }))}
              >
                {backendSubstores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name || store.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={newEmployee.employment_type}
                label="Employment Type"
                onChange={(e) => setNewEmployee((emp) => ({ ...emp, employment_type: e.target.value }))}
              >
                <MenuItem value="full_time">Full Time</MenuItem>
                <MenuItem value="part_time">Part Time</MenuItem>
              </Select>
            </FormControl>
            {/* numeric input for work hours */}
            <TextField
              label="Work Hours"
              type="number"
              value={newEmployee.work_hours}
              onChange={(e) => setNewEmployee((emp) => ({ ...emp, work_hours: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Address"
              value={newEmployee.address}
              onChange={(e) => setNewEmployee((emp) => ({ ...emp, address: e.target.value }))}
              required
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button
            onClick={handleAddEmployee}
            variant="contained"
            disabled={!(newEmployee.username && newEmployee.email && newEmployee.phone && newEmployee.substore_id && newEmployee.employment_type && newEmployee.work_hours && newEmployee.address)}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={closeRemoveDialog} maxWidth="xs">
        <DialogTitle>Remove Employee</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this employee?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog}>Cancel</Button>
          <Button onClick={handleRemoveEmployee} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <UploadExcelPopup
        open={uploadOpen}
        onClose={handleCloseUpload}
        onUpload={handleExcelUpload}
        uploadStatus={uploadStatus}
      />
    </MainCard>
  );
}
